import { useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoyaltyCard } from '../loyaltyApi';
import { walletNav, walletOrder, walletUrlIsStale, type WalletPlatform } from '../walletPass';

interface Props {
  card: LoyaltyCard;
  /** When the card was read (RTK `fulfilledTimeStamp`): decides whether the signed URL may have expired. */
  fetchedAt?: number;
  /** Re-reads the card and resolves with the fresh payload (new signed URLs). */
  refetch: () => Promise<LoyaltyCard>;
}

function urlOf(card: LoyaltyCard, platform: WalletPlatform): string | undefined {
  return platform === 'apple' ? card.apple_pass_url : card.google_save_url;
}

// A generic card glyph, white on black: the store badges' own artwork is
// trademarked, so this is the official STYLE (black pill, white label), not a copy.
function CardGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3 10h18" />
      <rect x="6" y="14" width="5" height="2" rx="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * « Ajouter à Apple Wallet » / « Ajouter à Google Wallet » under the member card
 * (SC-1091, spec §7.4). Only the platforms the hub reports as available
 * (`wallet_passes`), ordered by device. The URLs are signed and expire in 10 minutes:
 * a link older than 8 is never followed blindly — the card is re-read first.
 * Apple: same tab (Safari opens the add sheet). Google: a new tab.
 */
export default function WalletPassButtons({ card, fetchedAt, refetch }: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<WalletPlatform | null>(null);
  const [failed, setFailed] = useState(false);

  const platforms = useMemo(
    () => walletOrder(navigator.userAgent, navigator.maxTouchPoints).filter((p) => card.wallet_passes?.[p] === true),
    [card.wallet_passes],
  );
  if (platforms.length === 0) return null;

  const follow = async (event: MouseEvent<HTMLAnchorElement>, platform: WalletPlatform) => {
    const url = urlOf(card, platform);
    // A fresh URL: let the browser follow the plain link.
    if (url && !walletUrlIsStale(fetchedAt)) return;

    event.preventDefault();
    if (busy) return;
    setBusy(platform);
    setFailed(false);
    // Google opens a new tab: it must be opened inside the tap (popup blockers),
    // before the re-read below, and pointed at the fresh URL afterwards.
    const tab = platform === 'google' ? walletNav.openBlank() : null;
    try {
      const fresh = await refetch();
      const freshUrl = urlOf(fresh, platform);
      if (!freshUrl) throw new Error('no wallet url');
      if (platform === 'apple') walletNav.assign(freshUrl);
      else walletNav.openIn(tab, freshUrl);
    } catch {
      tab?.close();
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="wallet-pass-buttons" className="loyalty-scope mx-auto w-full max-w-[420px]">
      <div className="flex flex-wrap justify-center gap-3">
        {platforms.map((platform) => (
          <a
            key={platform}
            href={urlOf(card, platform) ?? '#'}
            target={platform === 'google' ? '_blank' : undefined}
            rel={platform === 'google' ? 'noopener noreferrer' : undefined}
            onClick={(e) => follow(e, platform)}
            aria-busy={busy === platform}
            data-testid={`wallet-pass-${platform}`}
            className="inline-flex min-h-[48px] min-w-[240px] items-center justify-center gap-2 rounded-[10px] border border-solid border-black bg-black px-4 font-sans text-[15px] font-semibold text-white no-underline"
          >
            <CardGlyph />
            {t(platform === 'apple' ? 'loyalty.walletPass.apple' : 'loyalty.walletPass.google')}
          </a>
        ))}
      </div>
      {failed && (
        <p role="alert" data-testid="wallet-pass-error" className="m-0 mt-2 text-center font-mono text-xs text-sale-red">
          {t('loyalty.walletPass.error')}
        </p>
      )}
    </div>
  );
}
